import React, { useCallback, useContext } from 'react';
import { ManualCustomListSelectorCode, selectorConfigByType } from '../../configure/lists/ListGroups';
import useCustomLists from '../../../custom-lists/useCustomLists';
import { DowJonesCustomListCode } from '../../configure/ScreeningDataSetup';
import {
  useQueryScreeningSelectors,
  useQueryScreeningSelectorsGroups,
} from '../../configure/lists/useQueryScreeningSelectors';
import { ScreenableFieldSetupContext } from '../helpers/useScreenableFieldSetupContextManager';
import { SalvProgressCircle, SalvTab, SalvTabList, SalvTabPanel, SalvTabs } from '@salvapp/ui-components';
import Selector from '../../configure/lists/Selector';
import './GroupSelectionStep.scss';
import InternalLink from '../../../common/InternalLink';

const GroupSelectionStep = () => {
  const { selectorGroupCodes, updateSelectorGroupCodes } = useContext(ScreenableFieldSetupContext);

  const updateCodes = useCallback(
    (code: string) => (isToggled: boolean) => {
      const codes = selectorGroupCodes ? [...selectorGroupCodes] : [];
      if (isToggled) {
        updateSelectorGroupCodes([...codes, code]);
      } else {
        updateSelectorGroupCodes(codes.filter((it) => it !== code));
      }
    },
    [selectorGroupCodes, updateSelectorGroupCodes]
  );

  const customLists = useCustomLists();
  const dowJonesListId = customLists.find((list) => list.code === DowJonesCustomListCode.DOW_JONES_SANCTION)?.id;
  const { data: unknownScreeningSelectors, isSuccess: isScreeningSelectorsLoaded } =
    useQueryScreeningSelectors(dowJonesListId);
  const screeningSelectors = unknownScreeningSelectors?.filter(
    (selector) => selectorConfigByType[selector.code as ManualCustomListSelectorCode]
  );
  const { data: screeningSelectorsGroups, isSuccess: isScreeningSelectorGroupsLoaded } =
    useQueryScreeningSelectorsGroups(dowJonesListId);

  return (
    <div className='group-selection-step'>
      <div className={'p-x-24 p-t-24 p-b-8'}>
        <h4 className='text-strong-m chapter-title'>Screened against</h4>
        <span className='text-s chapter-subtitle m-t-4'>
          Select List Groups to be used in this Screening Flow. If you haven’t defined any List Groups yet, you can do
          so <InternalLink title={'here'} link={'/screening/list-groups'} />.
        </span>
      </div>
      <div className='tab-wrapper'>
        {!isScreeningSelectorsLoaded || !isScreeningSelectorGroupsLoaded ? (
          <SalvProgressCircle />
        ) : (
          <SalvTabs>
            <SalvTabList>
              {screeningSelectors?.map((selector) => (
                <SalvTab
                  key={selector.code}
                  value={selector.code}
                  title={selectorConfigByType[selector.code as ManualCustomListSelectorCode].fullName}
                />
              ))}
            </SalvTabList>
            {screeningSelectors?.map((selector) => (
              <SalvTabPanel key={selector.code} value={selector.code}>
                <Selector
                  type={selector.code as ManualCustomListSelectorCode}
                  apiSelectorOptions={selector.options}
                  apiScreeningSelectorGroups={screeningSelectorsGroups.filter(
                    (group) => group.selectorCode === selector.code
                  )}
                  onSelectGroupCode={updateCodes}
                  selectedGroupCodes={selectorGroupCodes ?? []}
                />
              </SalvTabPanel>
            ))}
          </SalvTabs>
        )}
      </div>
    </div>
  );
};

export default GroupSelectionStep;
